import React from "react";

import { isEmpty, uniqBy } from "lodash/fp";

import TranscriptTurn, {
  TranscriptTurnAnnotations,
} from "@app/analytics/MeetingReport/components/TranscriptTurn";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import { VirtuosoShim } from "@app/analytics/MeetingReport/Transcript/styles";
import type {
  MeetingReportSectionContext,
  TranscriptItem,
} from "@app/analytics/MeetingReport/types";
import { isTurnParagraph } from "@app/analytics/MeetingReport/utils";
import { isNewParagraph } from "@app/analytics/MeetingReport/utils/editUtils";
import type {
  ReactionHighlight,
  TranscriptTurnParagraph,
} from "@app/common/types";
import { titleize } from "@app/utils/utils";

import { Column } from "@rui/Stack";

import EditableTranscriptTurnItemContent from "./EditableTranscriptTurnItemContent";
import ReadonlyTranscriptTurnItemContent from "./ReadonlyTranscriptTurnItemContent";

const EMPTY_REACTIONS: ReactionHighlight[] = [];

const getTurnAnnotations = (reactions: ReactionHighlight[]) => {
  const annotations = reactions.map((reaction) => ({
    direction: reaction.score.direction,
    label: `${
      reaction.score.direction === "positive" ? "Increased" : "Decreased"
    } ${titleize(reaction.score.metric)}`,
  }));
  return uniqBy("label", annotations);
};

const shouldHideItem = (
  paragraph: TranscriptTurnParagraph,
  previousItem?: TranscriptItem
): boolean => {
  if (paragraph.isDeleted) {
    // if the paragraph was added and deleted within the same editing session,
    // just hide it after it's been deleted.
    if (isNewParagraph(paragraph)) return true;

    // otherwise, only hide this paragraph if the previous paragraph is
    // part of the same turn, was also deleted, and is not hidden
    if (!previousItem?.isDeleted) return false;

    return (
      isTurnParagraph(previousItem) &&
      previousItem.turn.id === paragraph.turn.id &&
      !shouldHideItem(previousItem)
    );
  }
  return false;
};

const TranscriptTurnItem = ({
  context,
  paragraph,
  onClick,
  previousItem,
  nextItem,
}: {
  context: MeetingReportSectionContext;
  paragraph: TranscriptTurnParagraph & { focused?: boolean };
  onClick?: () => void;
  previousItem?: TranscriptItem;
  nextItem?: TranscriptItem;
}) => {
  const { editAccessLevel, inEditMode, onEditSpeakerClick } = useEditStore(
    (state) => ({
      editAccessLevel: state.editAccessLevel,
      inEditMode: state.editingSection === "transcript",
      onEditSpeakerClick: state.onEditSpeakerClick,
    })
  );

  const reactionsForParagraph = useTranscriptStore((state) =>
    state.reactionsEnabled
      ? state.reactionsByParagraphId.get(paragraph.id) ?? EMPTY_REACTIONS
      : EMPTY_REACTIONS
  );

  const hasReactions = !isEmpty(reactionsForParagraph);

  if (inEditMode && shouldHideItem(paragraph, previousItem)) {
    return <VirtuosoShim />;
  }

  const isSpeakerEditable =
    context.section === "main" && editAccessLevel === "full";
  const includeSpeaker =
    !isTurnParagraph(previousItem) ||
    previousItem.turn.id !== paragraph.turn.id ||
    previousItem.isDeleted;

  return (
    <Column>
      <TranscriptTurn
        id={paragraph.id}
        turnId={paragraph.turn.id}
        speaker={includeSpeaker ? paragraph.turn.speaker : undefined}
        timestamp={paragraph.turn.relativeStartTime.formatted}
        isDeleted={paragraph.isDeleted}
        // hack; we determine whether the turn is edited based on the presence
        // of an "editedBy" field, but that's not currently being returned
        // from the API, so use a non-empty string to trigger the "isEdited" state
        // TODO (ENG-10852): use the real editedBy value
        editedBy={paragraph.turn.isEdited ? " " : undefined}
        isSpeakerEditable={isSpeakerEditable}
        onSpeakerClick={onEditSpeakerClick}
      >
        {paragraph.isDeleted ? null : inEditMode ? (
          <EditableTranscriptTurnItemContent
            paragraph={paragraph}
            nextItem={nextItem}
          />
        ) : (
          <ReadonlyTranscriptTurnItemContent
            paragraph={paragraph}
            reactions={reactionsForParagraph}
            onClick={onClick}
          />
        )}
      </TranscriptTurn>
      {hasReactions && (
        <TranscriptTurnAnnotations
          annotations={getTurnAnnotations(reactionsForParagraph)}
        />
      )}
    </Column>
  );
};

export default TranscriptTurnItem;
